'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Lock, Mail, ArrowRight, Loader2 } from 'lucide-react';
import Image from '@/components/shared/Image';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        setError('Invalid email or password');
      } else {
        navigate('/admin');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-navy/5 rounded-full blur-3xl" />
      
      <div className="w-full max-w-[1100px] flex items-center justify-center gap-12 px-6 py-12 relative z-10">
        {/* Left Side - Brand Info (Hidden on mobile) */}
        <div className="hidden lg:flex flex-col flex-1 max-w-[450px]">
          <div className="mb-8">
            <Image
              src="/images/simisi/logo-simisi.svg"
              alt="Simisi&Co."
              width={200}
              height={50}
              className="h-[45px] w-auto brightness-0"
              priority
            />
          </div>
          <h1 className="text-4xl font-bold text-navy leading-tight mb-6">
            Content Management <span className="text-primary">System</span>
          </h1>
          <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
            Welcome to Simisi&Co. Admin Portal. Manage your insights, team members, and services with ease.
          </p>
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border shadow-sm">
              <div className="bg-muted p-2 rounded-lg text-primary">
                <Lock size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-navy">Secure Access</h3>
                <p className="text-sm text-muted-foreground">Industry-standard encryption for your data.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Card */}
        <Card className="w-full max-w-[420px] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border-0 overflow-hidden rounded-2xl">
          <div className="h-2 bg-gradient-to-r from-navy via-primary to-primary" />
          
          <CardHeader className="space-y-2 pt-8">
            <div className="lg:hidden mb-4">
              <Image
                src="/images/simisi/logo-simisi.svg"
                alt="Simisi&Co."
                width={120}
                height={30}
                className="h-[30px] w-auto mx-auto brightness-0"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-navy">Sign In</CardTitle>
            <CardDescription className="text-muted-foreground">
              Please enter your credentials to access the CMS
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-5">
              {error && (
                <Alert variant="destructive" className="bg-destructive/10 border-destructive/20 text-destructive animate-in fade-in slide-in-from-top-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[13px] font-semibold text-navy uppercase tracking-wider">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@simisi.id"
                    className="pl-10 h-12 border-border focus:border-primary focus:ring-primary/20 transition-all rounded-xl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-[13px] font-semibold text-navy uppercase tracking-wider">Password</Label>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-12 border-border focus:border-primary focus:ring-primary/20 transition-all rounded-xl"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter className="pb-8 pt-2">
              <Button 
                className="w-full h-12 bg-navy hover:bg-navy/90 text-white font-semibold text-base rounded-xl transition-all shadow-lg shadow-navy/20 group"
                type="submit" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
          
          <div className="bg-muted/30 border-t border-border p-4">
            <p className="text-center text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} Simisi&Co. All rights reserved.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
